import { FormGroup, Validators } from '@angular/forms';
import { FormConfig, FormConfigStateValue } from '@shared/interfaces/form-configs/form-config';

/**
 * Applies a FormConfig to a FormGroup, setting values, required validators, and disabled states
 * as specified in the config.
 *
 * @param form The FormGroup to apply the configuration to
 * @param config The FormConfig containing the field configurations
 * @param overwrite When true, always sets values even if the control already has one.
 *                  Defaults to false (only fills empty controls) — used by type configs
 *                  to avoid clobbering user input. Pass true for template application so
 *                  templates reliably replace any previously applied type-config values.
 * @returns The updated FormGroup
 */
export function applyFormConfig<T>(form: FormGroup, config: FormConfig<T>, overwrite = false): FormGroup {
  // Process each field in the config
  for (const [path, fieldConfig] of Object.entries(config) as [string, any][]) {
    const control = getNestedControl(form, path);

    if (!control) {
      if (!fieldConfig.optional) console.warn(`Control not found for path: ${path}`);
      continue;
    }

    // Apply value if provided in config.
    // Type configs preserve existing input unless the individual field must replace
    // a value that is incompatible with the newly selected type.
    if (fieldConfig.value !== undefined && (overwrite || fieldConfig.overwriteValue || !control.value)) {
      control.setValue(fieldConfig.value);
    }

    // Apply state (required/disabled)
    if (fieldConfig.state) {
      applyState(control, fieldConfig.state);
    }

    // Apply conditional disabling
    if (fieldConfig.disableCondition) {
      if (fieldConfig.disableCondition()) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    }
  }

  return form;
}

/**
 * Gets a nested form control from a form group using a dot-notation path
 *
 * @param form The FormGroup to get the control from
 * @param path The dot-notation path to the control
 * @returns The FormControl if found, or null if not found
 */
function getNestedControl(form: FormGroup, path: string) {
  const parts = path.split('.');
  let current = form;

  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    current = current.get(part) as FormGroup;

    if (!current) {
      return null;
    }
  }

  return current.get(parts.at(-1)!);
}

/**
 * Applies a state to a form control
 *
 * @param control The control to apply the state to
 * @param state The state to apply
 */
function applyState(control: any, state: FormConfigStateValue) {
  switch (state) {
    case 'disabled':
      control.disable({ emitEvent: false });
      break;
    case 'optional':
      control.removeValidators(Validators.required);
      control.enable();
      break;
    case 'required':
      const validators = control.validator ? [control.validator, Validators.required] : Validators.required;
      control.setValidators(validators);
      control.updateValueAndValidity({ emitEvent: false });
      control.enable()
      break;
  }
}
